import semver from "semver";
import { LocalCliError } from "./errors.js";

/** The public GHCR repo the platform-update path tracks by default. */
export const DEFAULT_IMAGE_REPO = "ghcr.io/m8t-labs/m8t";

export interface ParsedImageRef { registry: string; repo: string; tag: string | undefined; digest: string | undefined }

/**
 * Split "ghcr.io/m8t-labs/m8t:v0.1.0" into registry/repo/tag/digest. All four
 * combinations of tag and digest are legal refs, and the installer's paved path now
 * deploys the digest-only form.
 *
 * The digest comes off FIRST. A digest ref embeds its own ':' inside "sha256:", so
 * a last-colon split lands in the middle of it and yields repo ".../m8t@sha256"
 * with a 64-hex "tag" — which reads as a foreign registry to every repo comparison
 * downstream. That misparse is what silently classified a digest-pinned gateway as
 * BYOC and dropped it off the update rail.
 *
 * After the digest is removed, the tag is the segment after the last ':' only when
 * that ':' follows the last '/', so a registry port (host:5000/repo) is still not
 * mistaken for a tag.
 */
export function parseImageRef(ref: string): ParsedImageRef {
  let rest = ref;
  let digest: string | undefined;
  const at = rest.lastIndexOf("@");
  if (at !== -1) {
    digest = rest.slice(at + 1) || undefined;
    rest = rest.slice(0, at);
  }
  const lastColon = rest.lastIndexOf(":");
  const lastSlash = rest.lastIndexOf("/");
  let name = rest;
  let tag: string | undefined;
  if (lastColon > lastSlash) {
    name = rest.slice(0, lastColon);
    tag = rest.slice(lastColon + 1) || undefined;
  }
  return { registry: name.split("/")[0], repo: name, tag, digest };
}

/**
 * The digest a gateway roll or converge-deploy must bind to, from the release
 * manifest's own gateway pin — the verified artifact this converge already reads.
 * There is no second source and no tag fallback: falling back would redeploy the
 * install onto a mutable tag, which is the exact exposure the digest binding exists
 * to remove, at the moment the manifest is least trustworthy.
 *
 * Checked for presence and the `sha256:` prefix rather than the full 64-hex shape.
 * A manifest is generated by scripts/manifest-gen.mjs from a live registry
 * resolution and schema-validated at cut time, so the realistic failure is an
 * absent or empty digest, not a syntactically odd one — and a syntactically odd one
 * still fails loudly, at the registry, when the pull cannot resolve it. (The
 * installer's own descriptor check IS strict-shape; it defends a different artifact.)
 */
export function assertGatewayDigest(c: { ref: string; tag: string; digest?: string }): string {
  if (!c.digest?.startsWith("sha256:")) {
    throw new LocalCliError({
      code: "PLATFORM_GATEWAY_DIGEST_MISSING",
      message: `The release manifest's gateway pin (${c.ref}:${c.tag}) carries no usable image digest.`,
      hint: "Re-fetch the release channel, or target a release whose manifest pins a gateway digest. Deploying by tag instead is not offered: a mutable tag cannot establish which bytes run.",
    });
  }
  return c.digest;
}

/** The gateway image ref a converge deploys, composed from the manifest pin BY DIGEST. */
export function gatewayRefFromComponent(c: { ref: string; tag: string; digest?: string }): string {
  return `${c.ref}@${assertGatewayDigest(c)}`;
}

const SEMVER_TAG = /^v(\d+)\.(\d+)\.(\d+)$/;

export function isSemverTag(tag: string): boolean {
  return SEMVER_TAG.test(tag);
}

export function parseSemver(tag: string): [number, number, number] | null {
  const m = SEMVER_TAG.exec(tag);
  return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : null;
}

/** -1 if a<b, 0 if equal, 1 if a>b. Both must be vX.Y.Z. */
export function compareSemver(a: string, b: string): number {
  const pa = parseSemver(a);
  const pb = parseSemver(b);
  if (!pa || !pb) {
    throw new LocalCliError({
      code: "PLATFORM_BAD_SEMVER",
      message: `compareSemver needs vX.Y.Z tags, got '${a}' / '${b}'.`,
    });
  }
  for (let i = 0; i < 3; i++) {
    if (pa[i] !== pb[i]) return pa[i] < pb[i] ? -1 : 1;
  }
  return 0;
}

/**
 * -1 / 0 / 1 over PLATFORM VERSIONS, pre-release aware. Accepts bare,
 * v-prefixed and platform-v tag forms.
 *
 * This exists ALONGSIDE compareSemver rather than replacing it, because the two
 * compare different things and want different rules:
 *
 *   compareSemver          IMAGE TAGS (v0.4.11). Strictly X.Y.Z, and it must
 *                          STAY strict — pickNewestSemver filters through
 *                          isSemverTag, so a canary image tag
 *                          (v0.4.11-canary.5) is invisible to the auto-roll
 *                          path in planUpdate. That is the property that stops
 *                          an install ever auto-rolling onto a canary image,
 *                          and it is free only while these stay narrow.
 *
 *   comparePlatformVersion PLATFORM VERSIONS (0.7.2-canary.5). The canary
 *                          channel publishes pre-releases, so the forward-only
 *                          guard has to be able to order them.
 *
 * Throws on an unparseable input, exactly as compareSemver does: a version the
 * rail cannot order is a publishing bug, and answering "not forward" would let
 * it pass as an ordinary refusal.
 *
 * The grammar itself is owned by @m8t-stack/platform-release's canary-version
 * module; this only orders what that defines.
 */
export function comparePlatformVersion(a: string, b: string): number {
  const bare = (v: string) => v.trim().replace(/^platform-/, "").replace(/^v/, "");
  const va = semver.valid(bare(a));
  const vb = semver.valid(bare(b));
  if (!va || !vb) {
    throw new LocalCliError({
      code: "PLATFORM_BAD_SEMVER",
      message: `comparePlatformVersion needs platform versions, got '${a}' / '${b}'.`,
    });
  }
  return semver.compare(va, vb);
}

/** The highest vX.Y.Z tag, or null when none of `tags` is a semver tag. */
export function pickNewestSemver(tags: string[]): string | null {
  const semver = tags.filter(isSemverTag);
  if (semver.length === 0) return null;
  return semver.reduce((best, t) => (compareSemver(t, best) > 0 ? t : best));
}

export interface ParsedResourceId { subscriptionId: string; resourceGroup: string; name: string }

export function parseContainerAppResourceId(id: string): ParsedResourceId {
  const m =
    /\/subscriptions\/([^/]+)\/resourceGroups\/([^/]+)\/providers\/Microsoft\.App\/containerApps\/([^/]+)/i.exec(
      id,
    );
  if (!m) {
    throw new LocalCliError({
      code: "PLATFORM_BAD_RESOURCE_ID",
      message: `Could not parse a Container App resource id: ${id}`,
      hint: "Re-run discovery with 'm8t whoami', or pass --subscription.",
    });
  }
  return { subscriptionId: m[1], resourceGroup: m[2], name: m[3] };
}

export type UpdatePlan =
  | { kind: "noop"; current: string; available: string }
  | { kind: "roll"; current: string; available: string; target: string; repo: string }
  | { kind: "no-versions"; current: string }
  | { kind: "refuse-byoc"; current: string; currentRepo: string; trackedRepo: string }
  | { kind: "tag-not-found"; current: string; requested: string };

/**
 * Pure decision: given the live image, the available GHCR tags, the tracked
 * repo, and an optional explicit `--to`, decide what `m8t platform update`
 * should do. Auto mode never downgrades; an explicit `--to` may roll backward
 * (deliberate rollback). A non-semver current (`:latest`/SHA) always rolls onto
 * the pinned version. A different repo (BYOC private ACR) is refused.
 */
export function planUpdate(opts: {
  currentImage: string;
  availableTags: string[];
  imageRepo: string;
  to?: string;
  /** The target release's gateway digest (from the verified release manifest).
   *  Supplied whenever the caller knows it; it is what makes "already there"
   *  decidable for a digest-pinned deployment, which carries no tag to compare. */
  toDigest?: string;
}): UpdatePlan {
  const cur = parseImageRef(opts.currentImage);
  // Display-only. A digest-pinned deployment genuinely has no tag, and its honest
  // identity — the one actually read off the deployment — is its digest. Never used
  // as a roll target (that's always `target`).
  const currentTag = cur.tag ?? cur.digest ?? "(none)";

  if (cur.repo !== opts.imageRepo) {
    return { kind: "refuse-byoc", current: currentTag, currentRepo: cur.repo, trackedRepo: opts.imageRepo };
  }

  if (opts.to && !opts.availableTags.includes(opts.to)) {
    return { kind: "tag-not-found", current: currentTag, requested: opts.to };
  }

  const available = opts.to ?? pickNewestSemver(opts.availableTags);
  if (!available) return { kind: "no-versions", current: currentTag };

  // Digest equality before every tag-based EQUALITY below (it does not precede the
  // availability checks above — a target tag missing from the registry still raises
  // there, even for a deployment already running that release's digest). A
  // digest-pinned deployment has no tag, so `cur.tag === available` is false for it
  // no matter what is running, and the auto-mode semver guard below cannot fire
  // either. Without this the plan says "roll" on every converge tick forever —
  // a new Container App revision each time, deploying the bytes already deployed.
  // It is also the strictest form of "already there" available: identical bytes,
  // not merely an identical label.
  if (cur.digest && opts.toDigest && cur.digest === opts.toDigest) {
    return { kind: "noop", current: currentTag, available };
  }

  if (cur.tag === available) return { kind: "noop", current: currentTag, available };

  // Auto mode (no --to): only roll when strictly newer, or when the current tag
  // is not a tracked semver (e.g. :latest / a SHA), which we migrate onto a pin.
  if (!opts.to) {
    const currentIsSemver = cur.tag ? isSemverTag(cur.tag) : false;
    // Safe: `available` came from pickNewestSemver (always a semver tag) and
    // currentIsSemver guards cur.tag — so compareSemver never sees a non-semver.
    // cur.tag is guaranteed non-null when currentIsSemver is true (checked above)
    if (currentIsSemver && cur.tag && compareSemver(available, cur.tag) <= 0) {
      return { kind: "noop", current: currentTag, available };
    }
  }
  return { kind: "roll", current: currentTag, available, target: available, repo: opts.imageRepo };
}

export function summarizePlan(plan: UpdatePlan): { current: string; available: string; verdict: string } {
  switch (plan.kind) {
    case "noop":
      return { current: plan.current, available: plan.available, verdict: "up to date" };
    case "roll":
      return { current: plan.current, available: plan.available, verdict: "update available" };
    case "no-versions":
      return { current: plan.current, available: "-", verdict: "no published versions found" };
    case "refuse-byoc":
      return { current: plan.current, available: "-", verdict: `private-ACR image (${plan.currentRepo}) — not tracked` };
    case "tag-not-found":
      return { current: plan.current, available: plan.requested, verdict: `tag ${plan.requested} not published` };
  }
}

/**
 * Anonymously list tags for a PUBLIC GHCR repo. Needs no GitHub auth and no
 * docker — just HTTPS GETs (token exchange, then every v2 tags-list page). repo is
 * "ghcr.io/<owner>/<name>". `fetchImpl` is injectable for tests.
 */
export async function fetchPublicTags(
  repo: string,
  fetchImpl: typeof fetch = fetch,
): Promise<string[]> {
  if (!repo.startsWith("ghcr.io/")) {
    throw new LocalCliError({
      code: "PLATFORM_NOT_GHCR",
      message: `fetchPublicTags only supports ghcr.io repos, got ${repo}.`,
      hint: "Pass a ghcr.io/<owner>/<name> repo, or use the BYOC private-ACR deploy flow.",
    });
  }
  const path = repo.replace(/^ghcr\.io\//, ""); // "m8t-labs/m8t"
  const tokenRes = await fetchImpl(`https://ghcr.io/token?scope=repository:${path}:pull`);
  if (!tokenRes.ok) {
    throw new LocalCliError({
      code: "PLATFORM_GHCR_TOKEN_FAILED",
      message: `Could not get an anonymous GHCR token for ${repo} (HTTP ${tokenRes.status.toString()}).`,
      hint: "If the package is still private, ask the operator to make it public (deploy/ghcr-package-visibility.md).",
    });
  }
  const { token } = (await tokenRes.json()) as { token?: string };
  if (!token) {
    throw new LocalCliError({
      code: "PLATFORM_GHCR_PRIVATE",
      message: `GHCR returned no anonymous token for ${repo} — the package is likely still private.`,
      hint: "Make the package public (deploy/ghcr-package-visibility.md), then retry.",
    });
  }
  const expectedPath = `/v2/${path}/tags/list`;
  let pageUrl = new URL(`https://ghcr.io${expectedPath}`);
  pageUrl.searchParams.set("n", "1000");
  const visited = new Set<string>();
  const tags = new Set<string>();
  const maxPages = 100;

  for (let page = 0; page < maxPages; page += 1) {
    const href = pageUrl.toString();
    if (visited.has(href)) {
      throw new LocalCliError({
        code: "PLATFORM_GHCR_TAGS_PAGINATION_LOOP",
        message: `GHCR returned a pagination loop while listing tags for ${repo}.`,
      });
    }
    visited.add(href);

    const tagsRes = await fetchImpl(href, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!tagsRes.ok) {
      throw new LocalCliError({
        code: "PLATFORM_GHCR_TAGS_FAILED",
        message: `Could not list tags for ${repo} (HTTP ${tagsRes.status.toString()}).`,
      });
    }
    const body = (await tagsRes.json()) as { tags?: unknown };
    if (body.tags !== undefined && (!Array.isArray(body.tags) || body.tags.some((tag) => typeof tag !== "string"))) {
      throw new LocalCliError({
        code: "PLATFORM_GHCR_TAGS_INVALID",
        message: `GHCR returned an invalid tags list for ${repo}.`,
      });
    }
    for (const tag of (body.tags ?? []) as string[]) tags.add(tag);

    const next = nextLink(tagsRes.headers.get("link"));
    if (!next) return [...tags];
    const nextUrl = new URL(next, pageUrl);
    if (
      nextUrl.origin !== "https://ghcr.io"
      || nextUrl.pathname !== expectedPath
      || nextUrl.username !== ""
      || nextUrl.password !== ""
      || nextUrl.hash !== ""
    ) {
      throw new LocalCliError({
        code: "PLATFORM_GHCR_TAGS_PAGINATION_UNSAFE",
        message: `GHCR returned an unsafe pagination link while listing tags for ${repo}.`,
      });
    }
    pageUrl = nextUrl;
  }
  throw new LocalCliError({
    code: "PLATFORM_GHCR_TAGS_PAGINATION_LIMIT",
    message: `GHCR returned more than ${maxPages.toString()} tag pages for ${repo}.`,
  });
}

function nextLink(header: string | null): string | null {
  if (!header) return null;
  for (const entry of header.split(/,(?=\s*<)/)) {
    const target = /^\s*<([^>]+)>/.exec(entry)?.[1];
    if (!target) continue;
    const rel = entry
      .split(";")
      .slice(1)
      .map((parameter) => /^\s*rel\s*=\s*"?([^";]+)"?\s*$/i.exec(parameter)?.[1])
      .find((value) => value !== undefined);
    if (rel?.split(/\s+/).includes("next")) return target;
  }
  return null;
}
